import { CarrotPlanFormStep } from "../CarrotPlanFormTypes"
import { CarrotPlanMediaItem, CarrotPlanMediaType, DisplayStatus, MenuItemType } from "../../../types/CarrotPlanOptions"
import { CarrotPlanFormOption, MenuItemOptionFields, TopicOptionFields } from "./useCarrotPlanMappings"
import { CarrotPlanMediaMenuItemIds, CarrotPlanMediaTypeId, CarrotPlanMediaTypes } from "../MediaTypes"

const readAndLearnMenuItemIds = [
  CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Article].menuItemId,
  CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Video].menuItemId,
  CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Guide].menuItemId,
  CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Group].menuItemId
]

type UseGetAllRecommendedItems = {
  allRecommendedArticles: CarrotPlanMediaItem[]
  allRecommendedVideos: CarrotPlanMediaItem[]
  allRecommendedGuides: CarrotPlanMediaItem[]
  allRecommendedGroups: CarrotPlanMediaItem[]
  allRecommendedPersonalInteractionMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  allRecommendedSelfServeResourceMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  allRecommendedFinancialInformationMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  readAndLearnMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  areThereRecommendations: boolean
}

export function useGetAllRecommendedItems({
  menuItemOptions,
  topicOptions,
  steps
}: {
  menuItemOptions: CarrotPlanFormOption<MenuItemOptionFields>[]
  topicOptions: CarrotPlanFormOption<TopicOptionFields>[]
  steps: CarrotPlanFormStep[]
}): UseGetAllRecommendedItems {
  const entityIdsForCurrentSteps = steps.map((step) => step.menuItem?.optionFields.entityId)
  const entryIdsForCurrentMediaSteps = steps.map((step) => step.content?.optionFields.entryId)

  const menuItemAlreadyExistsInSteps = (menuItem: CarrotPlanFormOption<MenuItemOptionFields>) =>
    entityIdsForCurrentSteps.includes(menuItem.optionFields.entityId)
  const isReadAndLearnMenuItem = (menuItem: CarrotPlanFormOption<MenuItemOptionFields>) =>
    CarrotPlanMediaMenuItemIds.includes(menuItem.optionFields.entityId)
  const mediaItemAlreadyExistsInSteps = (mediaItem: CarrotPlanMediaItem) =>
    entryIdsForCurrentMediaSteps.includes(mediaItem.entryId)

  const menuItemAllowedToBeRecommended = (menuItem: CarrotPlanFormOption<MenuItemOptionFields>) =>
    (menuItem.optionMetadata?.isRecommended &&
      menuItem.optionMetadata?.displayStatus === DisplayStatus.SHOW &&
      !menuItemAlreadyExistsInSteps(menuItem)) ||
    isReadAndLearnMenuItem(menuItem)

  const allRecommendedMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[] = menuItemOptions.filter((menuItem) =>
    menuItemAllowedToBeRecommended(menuItem)
  )
  const allRecommendedPersonalInteractionMenuItems = []
  const allRecommendedSelfServeResourceMenuItems = []
  const allRecommendedFinancialInformationMenuItems = []
  const readAndLearnMenuItems = []

  allRecommendedMenuItems.forEach((menuItem) => {
    switch (menuItem.optionMetadata.menuItemType) {
      case MenuItemType.FINANCIAL_INFORMATION:
        allRecommendedFinancialInformationMenuItems.push(menuItem)
        break
      case MenuItemType.PERSONAL_INTERACTION:
        if (readAndLearnMenuItemIds.includes(menuItem.optionFields.entityId)) {
          readAndLearnMenuItems.push(menuItem)
        } else {
          allRecommendedPersonalInteractionMenuItems.push(menuItem)
        }
        break
      case MenuItemType.SELF_SERVE_RESOURCE:
        if (readAndLearnMenuItemIds.includes(menuItem.optionFields.entityId)) {
          readAndLearnMenuItems.push(menuItem)
        } else {
          allRecommendedSelfServeResourceMenuItems.push(menuItem)
        }
        break
    }
  })

  const allRecommendedMediaItems: CarrotPlanMediaItem[] = topicOptions
    .flatMap((topicOption) =>
      topicOption.optionFields.options
        .filter(
          (option) =>
            option.optionMetadata?.isRecommended &&
            option.optionMetadata.displayStatus === DisplayStatus.SHOW &&
            !mediaItemAlreadyExistsInSteps(option)
        )
        .map((option) => ({ ...option, subCategory: topicOption.label }))
    )
    .filter((menuItem, index, array) => array.findIndex((element) => element.entryId === menuItem.entryId) === index)

  const allRecommendedArticles = []
  const allRecommendedVideos = []
  const allRecommendedGuides = []
  const allRecommendedGroups = []

  allRecommendedMediaItems.forEach((mediaItem) => {
    switch (mediaItem.contentType) {
      case CarrotPlanMediaType.Article:
        allRecommendedArticles.push(mediaItem)
        break
      case CarrotPlanMediaType.Video:
        allRecommendedVideos.push(mediaItem)
        break
      case CarrotPlanMediaType.Guide:
        allRecommendedGuides.push(mediaItem)
        break
      case CarrotPlanMediaType.Group:
        allRecommendedGroups.push(mediaItem)
        break
    }
  })

  return {
    allRecommendedArticles,
    allRecommendedVideos,
    allRecommendedGuides,
    allRecommendedGroups,
    allRecommendedPersonalInteractionMenuItems,
    allRecommendedSelfServeResourceMenuItems,
    allRecommendedFinancialInformationMenuItems,
    readAndLearnMenuItems,
    areThereRecommendations: Boolean(allRecommendedMenuItems.length || allRecommendedMediaItems.length)
  }
}
